import { ReactNode, useMemo, useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import SidebarIcon from './SidebarIcon';
import Sidebar from './Sidebar';
import { SidebarProvider, useSidebar } from './SidebarContext';
import { apiFetch } from '../api';
import { useDarkMode } from '../contexts/DarkModeContext';
import TrialModuleBanner from './TrialModuleBanner';
import MaintenanceToggle from './MaintenanceToggle';
import MaintenanceBanner from './MaintenanceBanner';
import DmsUrgentModal from './DmsUrgentModal';
import SeasonalEffect from './SeasonalEffect';
import ThemeEffectToggle from './ThemeEffectToggle';

interface ContentAreaProps {
  children: ReactNode;
  pageTitle: string;
  iconName: string;
  orgDisplay: string;
  user: string;
  setShowPasswordModal: (show: boolean) => void;
}

function ContentArea({
  children,
  pageTitle,
  iconName,
  orgDisplay,
  user,
  setShowPasswordModal,
}: ContentAreaProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { collapsed, mobileOpen, setMobileOpen } = useSidebar();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { unreadCount } = useChat();
  const location = useLocation();
  const isDashboard = location.pathname === '/';

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const isSuper = (() => {
    try {
      const org = localStorage.getItem('organization');
      const r = JSON.parse(localStorage.getItem('roles') || '[]');
      return org === 'SUPER' || r.includes('SuperAdmin');
    } catch {
      return false;
    }
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Fixed header - responsive with seasonal header effect */}
      <header
        className="bg-white dark:bg-gray-800 shadow-md border-b border-slate-200 dark:border-slate-800 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 z-30 fixed top-0 left-0 right-0 w-full select-none"
      >
        {/* Saisonaler Header-Effekt (auf Unterseiten NUR im Header aktiv) */}
        {!isDashboard && <SeasonalEffect variant="header" />}

        <div className="flex items-center justify-between w-full max-w-full relative z-20">
            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 min-w-0 flex-1">
              {/* Hamburger Button für Navigation auf allen Bildschirmgrößen */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMobileOpen((prev) => !prev);
                }}
                className="p-3 -ml-1.5 rounded-xl text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 focus:outline-none transition cursor-pointer select-none touch-manipulation z-30 relative"
                aria-label={mobileOpen ? 'Navigation schließen' : 'Navigation öffnen'}
                title={mobileOpen ? 'Navigation schließen' : 'Navigation öffnen'}
              >
                <svg className="w-6 h-6 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>

              <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 min-w-0">
                <div className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300 flex-shrink-0"><SidebarIcon name={iconName} className="w-4 h-4 sm:w-5 sm:h-5" variant="solid" /></div>
                <div className="min-w-0">
                  <div className="text-sm sm:text-base lg:text-xl font-semibold dark:text-white truncate">{pageTitle}</div>
                  {orgDisplay && <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:block">Org: {orgDisplay}</div>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 flex-shrink-0">
              {/* Chat Indicator */}
              <Link
                to="/chat"
                className="p-1 sm:p-1.5 lg:p-2 rounded-lg bg-white dark:bg-gray-800 shadow hover:shadow-md transition-shadow relative flex items-center justify-center text-gray-700 dark:text-gray-300"
                title="Portal Chat"
              >
                <span className="text-lg">💬</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[9px] font-bold px-1.5 py-0.5 min-w-[16px] text-center leading-none">
                    {unreadCount}
                  </span>
                )}
              </Link>
              {/* Wartungsmodus-Toggle (nur SUPER) */}
              <MaintenanceToggle />
              {/* Saisonaler Effekt-Schalter je User */}
              <ThemeEffectToggle />
              {/* Dark Mode Toggle */}
              <button
                type="button"
                onClick={toggleDarkMode}
                className="p-1 sm:p-1.5 lg:p-2 rounded-lg bg-white dark:bg-gray-800 shadow hover:shadow-md transition-shadow"
                aria-label="Dark Mode umschalten"
                title={isDarkMode ? 'Hell-Modus aktivieren' : 'Dunkel-Modus aktivieren'}
              >
                {isDarkMode ? (
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>

              <div className="hidden lg:block text-xs lg:text-sm text-gray-600 dark:text-gray-400">{user}</div>

              <div className="relative" ref={dropdownRef}>
                <button 
                  type="button"
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded bg-white dark:bg-gray-800 shadow flex items-center gap-1 sm:gap-2 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700hover:bg-gray-600 transition-colors" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDropdown(prev => !prev);
                  }}
                >
                  <span className="hidden sm:inline">Konto</span>
                  <span className="sm:hidden">⋮</span>
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/wiki"
                      className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600"
                      onClick={() => setShowDropdown(false)}
                    >
                      📖 Wiki
                    </Link>
                    <Link
                      to="/changelog"
                      className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600"
                      onClick={() => setShowDropdown(false)}
                    >
                      📋 Change-Log
                    </Link>
                    {isSuper && (
                      <Link
                        to="/admin/theme"
                        className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 font-semibold"
                        onClick={() => setShowDropdown(false)}
                      >
                        🎨 Theme & Effekte
                      </Link>
                    )}
                    <button
                      type="button"
                      className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600"
                      onClick={() => {
                        setShowDropdown(false);
                        setShowPasswordModal(true);
                      }}
                    >
                      Kennwort ändern
                    </button>
                    <button
                      type="button"
                      className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600"
                      onClick={() => (window.location.href = '/logout')}
                    >
                      Abmelden
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

      {/* spacer to push main content below the fixed header */}
      <div className="h-12 sm:h-14 lg:h-16" />

      {/* Wartungs-Overlay für nicht privilegierte Organisationen */}
      <MaintenanceBanner />
      <DmsUrgentModal />

      {/* Trial Module Banner & Lockout - nur für normale Organisationen, nicht für SUPER */}
      {localStorage.getItem('organization') !== 'SUPER' ? (
        <TrialModuleBanner>
          <main className="w-full overflow-x-hidden max-w-full">{children}</main>
        </TrialModuleBanner>
      ) : (
        <main className="w-full overflow-x-hidden max-w-full">{children}</main>
      )}
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isMandatoryChange, setIsMandatoryChange] = useState(false);

  // Check if password change is mandatory on mount
  useState(() => {
    const mustChange = localStorage.getItem('must_change_password') === 'true';
    if (mustChange) {
      setIsMandatoryChange(true);
      setShowPasswordModal(true);
    }
  });

  const user = (() => {
    try {
      return localStorage.getItem('user') || 'Gast';
    } catch (e) {
      return 'Gast';
    }
  })();

  const orgDisplay = (() => {
    try {
      // Prefer a friendly organization name if available, otherwise fall back to the stored code
      return localStorage.getItem('organization_name') || localStorage.getItem('organization') || '';
    } catch (e) {
      return '';
    }
  })();

  const location = useLocation();

  const pageTitle = useMemo(() => {
    const p = location.pathname || '/';
    const hash = (location.hash || '').replace('#', '');
    if (p === '/' || p === '') return 'Dashboard';
    if (p.startsWith('/pos-dashboard')) return 'POS Dashboard';
    if (p.startsWith('/pos')) return 'Kasse';
    if (p.startsWith('/lager')) return 'Warenlager';
    if (p.startsWith('/storage')) return 'Lagerverwaltung';
    if (p.startsWith('/balance')) return 'Guthaben-Verwaltung';
    if (p.startsWith('/profile')) return 'Profil';
    if (p.startsWith('/dienstplan-verwaltung')) return 'Dienstplan-Verwaltung';
    if (p.startsWith('/dienstplan')) return 'Dienstplan';
    if (p.startsWith('/my-expense-requests')) return 'Auslagen';
    if (p.startsWith('/expense-claims')) return 'Auslagen-Verwaltung';
    if (p.startsWith('/sonderdienste')) return 'Dienste & Veranstaltungen';
    if (p.startsWith('/trust-network')) return 'Vertrauensstellungen';
    if (p.startsWith('/shared-shifts/incoming')) return 'Fremde Dienste';
    if (p.startsWith('/bonuspunkte/admin') || p.startsWith('/admin/bonuspunkte')) return 'Bonuspunkte-Verwaltung';
    if (p.startsWith('/bonuspunkte')) return 'Bonuspunkte';
    if (p.startsWith('/poolwaesche/configure')) return 'Poolwäsche Konfiguration';
    if (p.startsWith('/poolwaesche/scanner')) return 'Poolwäsche Scanner';
    if (p.startsWith('/poolwaesche/inventory')) return 'Poolwäsche Inventar';
    if (p.startsWith('/poolwaesche/station-scanner')) return 'Stations-Scanner';
    if (p.startsWith('/poolwaesche/my-articles')) return 'Meine Kleidung';
    if (p.startsWith('/poolwaesche')) return 'Poolwäsche';
    if (p.startsWith('/chat')) return 'Chat';
    if (p.startsWith('/dms/verwaltung')) return 'DMS Verwaltung & Nachweise';
    if (p.startsWith('/dms')) return 'Dokumente & Richtlinien';
    if (p.startsWith('/fzg-mat-check/verwaltung')) return 'FZG & MAT Check Verwaltung';
    if (p.startsWith('/fzg-mat-check')) return 'FZG & MAT Check';
    if (p.startsWith('/lagekarte')) return 'Lagekarte (GIS & DV 102)';
    if (p.startsWith('/etb')) return 'Einsatztagebuch (ETB)';
    if (p.startsWith('/meldewesen')) return 'Meldewesen & Ticket-System';
    if (p.startsWith('/courses-events/administration')) return 'Kursverwaltung';
    if (p.startsWith('/courses-events')) return 'Kurse & Events';
    if (p.startsWith('/zeiterfassung')) return 'Zeiterfassung';
    if (p.startsWith('/statistiken')) return 'Statistiken';
    if (p.startsWith('/reports')) return 'Berichtswesen';
    if (p.startsWith('/wiki')) return 'Wiki & Leitfaden';
    if (p.startsWith('/changelog')) return 'Change-Log';
    if (p.startsWith('/admin/theme')) return 'Theme & Effekte';
    if (p.startsWith('/admin')) {
      if (hash === 'users') return 'Benutzer-Verwaltung';
      if (hash === 'roles') return 'Rollen & Berechtigungen';
      if (hash === 'modules') return 'Modul-Verwaltung';
      if (hash === 'orgs') return 'Organisations-Verwaltung';
      if (hash === 'logo') return 'Logo & Branding';
      if (hash === 'billing') return 'Billing & Pakete';
      if (hash === 'qualifikationen') return 'Qualifikationen-Verwaltung';
      if (hash === 'devices') return 'Externe Geräte';
      if (hash === 'change-requests') return 'Änderungsanträge';
      if (hash === 'document-categories') return 'Dokumentenkategorien';
      if (hash === 'document-templates') return 'Dokumentenvorlagen';
      if (hash === 'mail-accounts') return 'Mail-Konten';
      if (hash === 'tages-verwaltung') return 'Tages-Dashboard Verwaltung';
      if (hash === 'ticket-settings') return 'Ticket-Einstellungen';
      if (hash === 'dienstplan-verwaltung') return 'Dienstplan-Einstellungen';
      if (hash === 'diensttypen') return 'Diensttypen & Kategorien';
      if (hash === 'fahrzeug-verwaltung') return 'Fahrzeugverwaltung';
      return 'Administration';
    }
    return 'Dashboard';
  }, [location.pathname, location.hash]);

  const iconName = useMemo(() => {
    const p = location.pathname || '/';
    if (p === '/' || p === '') return 'home';
    if (p.startsWith('/pos-dashboard') || p.startsWith('/pos')) return 'pos';
    if (p.startsWith('/lager') || p.startsWith('/storage')) return 'storage';
    if (p.startsWith('/balance')) return 'balance';
    if (p.startsWith('/profile')) return 'users';
    if (p.startsWith('/dienstplan-verwaltung') || p.startsWith('/dienstplan')) return 'dienstplan';
    if (p.startsWith('/my-expense-requests') || p.startsWith('/expense-claims')) return 'balance';
    if (p.startsWith('/sonderdienste')) return 'events';
    if (p.startsWith('/trust-network') || p.startsWith('/shared-shifts')) return 'trust';
    if (p.startsWith('/bonuspunkte')) return 'bonus';
    if (p.startsWith('/poolwaesche')) return 'laundry';
    if (p.startsWith('/chat')) return 'chat';
    if (p.startsWith('/dms')) return 'dms';
    if (p.startsWith('/fzg-mat-check')) return 'fzg_mat_check';
    if (p.startsWith('/lagekarte')) return 'lagekarte';
    if (p.startsWith('/etb')) return 'etb';
    if (p.startsWith('/meldewesen')) return 'meldewesen';
    if (p.startsWith('/courses-events')) return 'courses_events';
    if (p.startsWith('/zeiterfassung')) return 'zeiterfassung';
    if (p.startsWith('/statistiken') || p.startsWith('/reports')) return 'statistics';
    if (p.startsWith('/wiki')) return 'wiki';
    if (p.startsWith('/changelog')) return 'changelog';
    if (p.startsWith('/admin')) return 'admin';
    return 'home';
  }, [location.pathname]);

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setPasswordError('Passwort muss mindestens ein Sonderzeichen enthalten');
      return;
    }

    // Check for consecutive duplicate characters
    for (let i = 0; i < newPassword.length - 1; i++) {
      if (newPassword[i] === newPassword[i + 1]) {
        setPasswordError('Passwort darf keine aufeinanderfolgenden identischen Zeichen enthalten');
        return;
      }
    }

    try {
      await apiFetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          oldPassword,
          newPassword,
        }),
      });
      setPasswordSuccess('Passwort erfolgreich geändert');
      localStorage.setItem('must_change_password', 'false');
      setIsMandatoryChange(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err: any) {
      setPasswordError(err?.message || 'Fehler beim Ändern des Passworts');
    }
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white w-full overflow-x-hidden flex flex-col lg:flex-row">
        <Sidebar />
        <div className="w-full min-w-0 flex-1">
          <ContentArea
            pageTitle={pageTitle}
            iconName={iconName}
            orgDisplay={orgDisplay}
            user={user}
            setShowPasswordModal={setShowPasswordModal}
          >
            {children}
          </ContentArea>
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isMandatoryChange && setShowPasswordModal(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-semibold mb-4 dark:text-white">
                {isMandatoryChange ? 'Passwortänderung erforderlich' : 'Passwort ändern'}
              </h2>
              {isMandatoryChange && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded text-sm text-amber-800 dark:text-amber-200">
                  Sie müssen Ihr Passwort ändern, bevor Sie fortfahren können.
                </div>
              )}
              {passwordError && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded text-sm text-green-700 dark:text-green-300">
                  {passwordSuccess}
                </div>
              )}
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Aktuelles Passwort
                  </label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Mindestens 8 Zeichen, mindestens ein Sonderzeichen, keine aufeinanderfolgenden identischen Zeichen.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Neues Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Passwort ändern
                  </button>
                  {!isMandatoryChange && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordModal(false);
                        setPasswordError('');
                        setOldPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
                    >
                      Abbrechen
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </SidebarProvider>
  );
}
